/**
 * Slice D round-2 audit-finding regression tests (integration level).
 *
 * These cover the three frontend findings from the Slice D FAIL round-2 verdict,
 * exercised through the real CanvasRenderer (and, for finding 1, the real builder
 * preview projection), not through the local layout-math helpers in isolation:
 *
 *   Finding 1 - the builder preview projection must carry parentElementId /
 *     parentPanelId / displayOrder, or a nested child mounts as a grid ROOT
 *     instead of inside its container.
 *   Finding 2 - the nested grid must read the product container discriminator
 *     (props.internalGrid.columns), honoring an authored 6-column nest and
 *     defaulting to 12 when none is authored.
 *   Finding 3 - with responsive mapping inactive (no `responsive`, no
 *     `forcedBreakpoint`) the grid must emit the plain pre-responsive literals,
 *     with no `--canvas-density-scale` reference (byte-identity to before).
 *
 * Observation channel: MUI Joy `sx` compiles to emotion classes, and in this
 * repo's jest env emotion inserts rules in SPEEDY mode, so the rule text is NOT
 * in <style> textContent -- it is in document.styleSheets[].cssRules[].cssText.
 * That CSSOM text is what these tests read. Column-count and density assertions
 * use a before/after occurrence delta so they are robust to emotion caching
 * rules across tests in the same file. Finding-1 assertions key on rendered text
 * (container empty-state vs the child's label), which needs no CSS at all.
 */
/* eslint-disable testing-library/render-result-naming-convention --
   projectRenderableElements is a pure projection helper, not a render() call.
   The testing-library heuristic misfires on its "Renderable" substring and
   demands the projected array be named `view`/`utils`; that would be a lie.
   No render() return value is ever captured in this file. */
import { render, screen } from "@testing-library/react";
import { CanvasRenderer } from "./CanvasRenderer";
import type { RenderableElement } from "./CanvasRenderer";
import type {
  CanvasGridConfig,
  CanvasElementProps,
  ContainerElementConfig,
} from "../../types";
import {
  projectRenderableElements,
  type CanvasBuilderElement,
} from "../../views/canvasBuilderProjection";

// Concatenate every CSS rule emotion has inserted, read from the CSSOM so it
// works in emotion's speedy mode (where <style> textContent is empty).
function cssomText(): string {
  let out = "";
  for (const sheet of Array.from(document.styleSheets) as CSSStyleSheet[]) {
    let rules: CSSRuleList | undefined;
    try {
      rules = sheet.cssRules;
    } catch {
      // Cross-origin or detached sheet; skip.
      continue;
    }
    if (!rules) continue;
    for (const rule of Array.from(rules)) out += rule.cssText + "\n";
  }
  return out;
}

function countOccurrences(haystack: string, needle: string): number {
  return haystack.split(needle).length - 1;
}

// ---------------------------------------------------------------------------
// Finding 1 - builder preview projection carries the nesting metadata
// ---------------------------------------------------------------------------

describe("Finding 1 - builder preview keeps nesting metadata", () => {
  const gridConfig: CanvasGridConfig = {
    columns: 10,
    rowHeight: 60,
    gap: 8,
    minRows: 4,
  };

  const containerBuilder: CanvasBuilderElement = {
    id: "container-db-1",
    type: "container",
    props: { label: "Section A" },
    layout: {},
    tempId: "container-1",
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 10,
    rowSpan: 2,
  };

  const childBuilder: CanvasBuilderElement = {
    id: "field-db-1",
    type: "field",
    props: { variant: "text-input", label: "Nested Child Field" },
    layout: {},
    tempId: "field-1",
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 12,
    rowSpan: 1,
    // The three fields the regression dropped.
    parentElementId: "container-1",
    parentPanelId: "panel-x",
    displayOrder: 3,
  };

  it("projectRenderableElements carries parentElementId / parentPanelId / displayOrder", () => {
    const projected = projectRenderableElements([containerBuilder, childBuilder]);
    const projChild = projected.find((e) => e.id === "field-1");
    expect(projChild).toBeTruthy();
    expect(projChild?.parentElementId).toBe("container-1");
    expect(projChild?.parentPanelId).toBe("panel-x");
    expect(projChild?.displayOrder).toBe(3);
  });

  it("mounts the projected child inside its container, not as a grid root", () => {
    const projected = projectRenderableElements([containerBuilder, childBuilder]);
    render(<CanvasRenderer elements={projected} config={gridConfig} mode="edit" />);
    // Child is nested -> its label renders and the container is not empty.
    // getByText throws if absent, so the call itself is the presence assertion.
    screen.getByText("Nested Child Field");
    expect(screen.queryByText("Drop elements here")).toBeNull();
  });

  it("control: dropping parentElementId promotes the child to a root and empties the container", () => {
    const orphan: CanvasBuilderElement = {
      ...childBuilder,
      parentElementId: undefined,
      parentPanelId: undefined,
    };
    const projected = projectRenderableElements([containerBuilder, orphan]);
    render(<CanvasRenderer elements={projected} config={gridConfig} mode="edit" />);
    // Container now has no children -> empty-state text appears; the child still
    // renders, but at the grid root. This proves the nesting metadata is
    // load-bearing for the observable above. getByText throws if absent, so each
    // call is itself the presence assertion.
    screen.getByText("Drop elements here");
    screen.getByText("Nested Child Field");
  });
});

// ---------------------------------------------------------------------------
// Finding 2 - nested grid reads the product container discriminator
// ---------------------------------------------------------------------------

describe("Finding 2 - nested grid reads props.internalGrid.columns", () => {
  // Root column count (10) is distinct from every nested count so a nested
  // repeat(6|12, 1fr) is never the root grid's own repeat(10, 1fr).
  const f2Config: CanvasGridConfig = {
    columns: 10,
    rowHeight: 62,
    gap: 8,
    minRows: 4,
  };

  const nestedChild: RenderableElement = {
    id: "n-field-1",
    type: "field",
    props: { variant: "text-input", label: "Nested Child" },
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 12,
    rowSpan: 1,
    parentElementId: "n-container-1",
  };

  const container = (cols?: number): RenderableElement => {
    const config: ContainerElementConfig = {
      containerType: "SECTION",
      title: "Sec",
      ...(cols ? { internalGrid: { columns: cols } } : {}),
    };
    return {
      id: "n-container-1",
      type: "container",
      props: config as unknown as CanvasElementProps,
      gridColumn: 1,
      gridRow: 1,
      columnSpan: 10,
      rowSpan: 2,
    };
  };

  it("honors an authored internalGrid.columns of 6 (nested grid = repeat(6, 1fr))", () => {
    const before = countOccurrences(cssomText(), "repeat(6, 1fr)");
    render(
      <CanvasRenderer
        elements={[container(6), nestedChild]}
        config={f2Config}
        mode="edit"
        forcedBreakpoint="desktop"
      />,
    );
    expect(countOccurrences(cssomText(), "repeat(6, 1fr)")).toBeGreaterThan(before);
  });

  it("defaults the nested grid to 12 columns when no internalGrid is authored (repeat(12, 1fr))", () => {
    const before = countOccurrences(cssomText(), "repeat(12, 1fr)");
    render(
      <CanvasRenderer
        elements={[container(undefined), nestedChild]}
        config={f2Config}
        mode="edit"
        forcedBreakpoint="desktop"
      />,
    );
    expect(countOccurrences(cssomText(), "repeat(12, 1fr)")).toBeGreaterThan(before);
  });
});

// ---------------------------------------------------------------------------
// Finding 3 - inactive path is byte-identical (no density-scale reference)
// ---------------------------------------------------------------------------

describe("Finding 3 - inactive path emits no --canvas-density-scale", () => {
  // A distinct config (rowHeight 61 / columns 9) so this describe's root grid
  // rule never collides with Finding 2's, keeping the deltas fresh.
  const f3Config: CanvasGridConfig = {
    columns: 9,
    rowHeight: 61,
    gap: 8,
    minRows: 4,
  };

  const soloRoot: RenderableElement = {
    id: "s-field-1",
    type: "field",
    props: { variant: "text-input", label: "Solo Field" },
    gridColumn: 1,
    gridRow: 1,
    columnSpan: 4,
    rowSpan: 1,
  };

  it("emits no --canvas-density-scale when responsive mapping is inactive", () => {
    const before = countOccurrences(cssomText(), "--canvas-density-scale");
    render(<CanvasRenderer elements={[soloRoot]} config={f3Config} mode="runtime" />);
    // This render must introduce no density-scale reference at all.
    expect(countOccurrences(cssomText(), "--canvas-density-scale")).toBe(before);
  });

  it("control: the active path DOES apply --canvas-density-scale", () => {
    const before = countOccurrences(cssomText(), "--canvas-density-scale");
    render(
      <CanvasRenderer
        elements={[soloRoot]}
        config={f3Config}
        mode="runtime"
        forcedBreakpoint="desktop"
      />,
    );
    // Same element, same config, only the responsive path differs -> density
    // scaling now appears. This proves the absence above is the inactive path,
    // not that the renderer never emits the variable.
    expect(countOccurrences(cssomText(), "--canvas-density-scale")).toBeGreaterThan(before);
  });
});
